import { useEffect, useState } from 'react';
import type { Embedding, ThemeEmbeddings } from './search.types';
import type { PrecomputedEmbeddingsBundle } from './precomputedEmbeddingsBundle.types';
import { EMBEDDINGS_STORE, buildThemeEmbeddings } from './precomputedEmbeddingsBundle';
import { useInitialThemeSearchIndex } from './ThemeSearchIndexProvider';
import { addError } from '../logger';

export { EMBEDDINGS_STORE };

type PrecomputedEmbeddingsResult = Readonly<{
  /** `undefined` until the bundle loads — or for good, if `loadFailed`. */
  themes: Map<string, ThemeEmbeddings> | undefined;
  /**
   * Whether `themes` carries vectors, not just keyword names. False for the server's inlined index
   * (PRNT-5582), which has the names style and color matching need but none of `themes.bin`.
   * Anything that scores by similarity must wait for this: an empty `concepts` scores 0, which is
   * indistinguishable from "nothing matched". Name matching may read `themes` without it.
   */
  vectorsReady: boolean;
  /** The bundle request failed; `themes` will not arrive on this mount. */
  loadFailed?: boolean;
  /** Style group key → its member keywords; empty until the bundle loads. Drives the Style filter. */
  keywordGroups: Readonly<Record<string, ReadonlyArray<string>>>;
  embeddingMap: Map<string, Embedding>;
  wellKnownEmbeddings: Readonly<{
    [K in keyof PrecomputedEmbeddingsBundle['keywordsWellKnown']]: Embedding;
  }>;
}>;

let precomputedEmbeddingsPromise: Promise<PrecomputedEmbeddingsResult> | undefined;
// The resolved bundle, so a component mounting after the load starts with it
// instead of rendering empty and again once its own `await` resumes.
let precomputedEmbeddings: PrecomputedEmbeddingsResult | undefined;

/** `fetch` resolves for a 404, so an unchecked read turns an error page into a length mismatch. */
async function fetchFile(file: string, read: 'json'): Promise<PrecomputedEmbeddingsBundle>;
async function fetchFile(file: string, read: 'arrayBuffer'): Promise<ArrayBuffer>;
async function fetchFile(file: string, read: 'json' | 'arrayBuffer') {
  const response = await fetch(`${EMBEDDINGS_STORE}/${file}`);
  if (!response.ok) throw new Error(`Embeddings bundle: ${file} returned ${response.status}`);
  return read === 'json' ? response.json() : response.arrayBuffer();
}

/**
 * Loads the full bundle, vectors included.
 *
 * Always fetches `themes.json` alongside `themes.bin`, even when the server inlined a copy
 * (PRNT-5582). The two files are one unit: `keywords` order and `keywordsWellKnown` are positional
 * offsets into the vector array, so pairing an inlined bundle with an independently fetched `.bin`
 * would decode new vectors through old indexes whenever the search pipeline republishes between
 * the two — silently scoring every concept and well-known embedding against the wrong vector. The
 * server's index can be an hour stale by design (`themeSearchIndex.ts`), which makes that window
 * wide. The inlined copy still does its job: it seeds the names-only first render synchronously,
 * which is what hydration needs. Re-reading 11 KB gzipped is the price of a consistent pair.
 */
export async function loadPrecomputedEmbeddings(): Promise<PrecomputedEmbeddingsResult> {
  const [bundle, bigBuffer] = await Promise.all([fetchFile('themes.json', 'json'), fetchFile('themes.bin', 'arrayBuffer')]);

  const embeddings = bundle.keywords.map((_, i) => {
    const keywordBuffer = bigBuffer.slice(i * bundle.embeddingSizeBytes, (i + 1) * bundle.embeddingSizeBytes);
    return Array.from(new Float32Array(keywordBuffer)) as Embedding;
  });

  const wellKnownEmbeddings = {
    beige: embeddings[bundle.keywordsWellKnown.beige],
    foil: embeddings[bundle.keywordsWellKnown.foil],
    free: embeddings[bundle.keywordsWellKnown.free],
    landscape: embeddings[bundle.keywordsWellKnown.landscape],
    photo: embeddings[bundle.keywordsWellKnown.photo],
    portrait: embeddings[bundle.keywordsWellKnown.portrait],
    qrCode: embeddings[bundle.keywordsWellKnown.qrCode],
    white: embeddings[bundle.keywordsWellKnown.white],
  };

  return {
    // Shared with the SSR server, which builds the same map without vectors (PRNT-5582).
    themes: buildThemeEmbeddings(bundle, embeddings),
    vectorsReady: true,
    keywordGroups: bundle.keywordGroups ?? {},
    embeddingMap: new Map(bundle.keywords.map((keyword, i) => [keyword, embeddings[i]])),
    wellKnownEmbeddings,
  };
}

const EMPTY_WELL_KNOWN_EMBEDDINGS: PrecomputedEmbeddingsResult['wellKnownEmbeddings'] = {
  beige: [],
  foil: [],
  free: [],
  landscape: [],
  photo: [],
  portrait: [],
  qrCode: [],
  white: [],
};

/** Nothing loaded yet. One shared, frozen-by-convention object: every field is empty and read-only. */
const NOT_LOADED_RESULT: PrecomputedEmbeddingsResult = {
  themes: undefined,
  vectorsReady: false,
  keywordGroups: {},
  embeddingMap: new Map<string, Embedding>(),
  wellKnownEmbeddings: EMPTY_WELL_KNOWN_EMBEDDINGS,
};

/**
 * The names-only result for one inlined bundle, built once and shared.
 *
 * Memoised on the bundle's identity because this hook is called per *tile* (`useTileAltText`) as
 * well as per facet hook: a 128-tile style page would otherwise build the same 1126-row map 128
 * times on the server and 128 more on the first client render. Safe to share — the map is only
 * ever read, and the stale-while-revalidate cache hands every request within its TTL the same
 * bundle object, so concurrent server renders reuse one map and a TTL refresh rebuilds once.
 */
let seededBundle: PrecomputedEmbeddingsBundle | undefined;
let seededResult: PrecomputedEmbeddingsResult | undefined;

const initialResult = (inlinedBundle: PrecomputedEmbeddingsBundle | undefined): PrecomputedEmbeddingsResult => {
  if (!inlinedBundle) return NOT_LOADED_RESULT;

  if (seededBundle !== inlinedBundle || !seededResult) {
    seededBundle = inlinedBundle;
    seededResult = {
      themes: buildThemeEmbeddings(inlinedBundle),
      vectorsReady: false,
      keywordGroups: inlinedBundle.keywordGroups ?? {},
      embeddingMap: new Map<string, Embedding>(),
      wellKnownEmbeddings: EMPTY_WELL_KNOWN_EMBEDDINGS,
    };
  }

  return seededResult;
};

export function usePrecomputedEmbeddings(): PrecomputedEmbeddingsResult {
  // Present only on a server-rendered style URL, where the first client render must reproduce the
  // server's grid before `themes.bin` can possibly have landed.
  const inlinedBundle = useInitialThemeSearchIndex();
  const [themesAndEmbeddings, setThemesAndEmbeddings] = useState<PrecomputedEmbeddingsResult>(() => precomputedEmbeddings ?? initialResult(inlinedBundle));

  useEffect(() => {
    // No early return when `precomputedEmbeddings` is set: it may have resolved after this
    // render read it. Re-setting the same object is a no-op state update.
    void (async () => {
      // PrecomputedEmbeddings are global to Joy. Only load once per app-instance.
      precomputedEmbeddingsPromise = precomputedEmbeddingsPromise ?? loadPrecomputedEmbeddings();

      try {
        precomputedEmbeddings = await precomputedEmbeddingsPromise;
        setThemesAndEmbeddings(precomputedEmbeddings);
      } catch (error) {
        // Not easy to rethrow here, so using addError instead.
        addError(error, { context: 'usePrecomputedEmbeddings' });
        precomputedEmbeddingsPromise = undefined; // Prevent re-awaiting a rejected promise on next mount.
        setThemesAndEmbeddings(current => ({ ...current, loadFailed: true }));
      }
    })();
  }, []);

  return themesAndEmbeddings;
}
